import { useCallback, useMemo } from 'react';
import { Select, SelectLayout } from '@amzn/stencil-react-components/select';
import type { OptionSelectItem } from './OptionSelect';

/**
 * Threshold above which Stencil recommends enabling the Select's
 * search field ("Don't use the searchable variation for less than ten
 * items"). Same rule {@link OptionSelect} applies to single-select.
 */
const SEARCHABLE_SELECT_THRESHOLD = 10;

/**
 * Reference-stable empty selection. Stencil's `Select` compares the
 * selected-options array by REFERENCE, and callers frequently derive
 * "nothing selected" fresh each render — an inline `[]` would make the
 * trigger flicker back to the placeholder mid-interaction. Callers that
 * pass no selection resolve to this one shared array.
 */
const NO_SELECTION: readonly OptionSelectItem[] = [];

export interface OptionMultiSelectProps {
  /**
   * Options to offer, in render order. Callers should memoise this
   * array: Stencil compares options by REFERENCE, so a fresh array each
   * render churns the dropdown's internal state.
   */
  readonly options: readonly OptionSelectItem[];
  /**
   * Currently-selected option `value`s. Values matching no option are
   * ignored (they render nothing) rather than crashing — the same
   * deactivated / cross-division read-back tolerance
   * {@link OptionSelect} has for its single value.
   */
  readonly values: readonly string[];
  /**
   * Receives the full new set of selected `value`s on every toggle.
   * Stencil owns the add/remove bookkeeping internally and hands us the
   * complete array each change, so callers replace rather than patch.
   */
  readonly onChange: (values: string[]) => void;
  /** Fires when focus leaves the control (dropdown close). */
  readonly onBlur?: (() => void) | undefined;
  readonly placeholder?: string | undefined;
  readonly disabled?: boolean | undefined;
  /** Renders the Select's error border and sets `aria-invalid`. */
  readonly hasError?: boolean | undefined;
  readonly ariaLabel?: string | undefined;
  /** Sets `aria-required` on the control. Visual asterisk is {@link RequiredLabel}'s job. */
  readonly isRequired?: boolean | undefined;
  readonly dataTestId?: string | undefined;
}

/**
 * The shared v2 multi-select: the same Stencil `Select` primitive
 * {@link OptionSelect} wraps, with `isMulti` turned on and an
 * array-valued surface. Reuses the {@link OptionSelectItem} shape, the
 * searchable-at-ten threshold, and the case-insensitive `label OR
 * value` search override so single- and multi-select behave
 * identically apart from cardinality.
 *
 * Selection bookkeeping (which options are on) lives inside Stencil —
 * `onChange` hands back the FULL selected-option array on every toggle,
 * so this wrapper is a pure value ⇄ option mapping with no local draft
 * state. Whoever needs "commit once when the auditor is done" wires
 * that to {@link onBlur} (dropdown close), matching V1 desktop.
 *
 * Deliberately renders NO label — pair with {@link RequiredLabel} when
 * a visible label is needed, same as {@link OptionSelect}.
 */
export function OptionMultiSelect(props: OptionMultiSelectProps): JSX.Element {
  const {
    options,
    values,
    onChange,
    onBlur,
    placeholder,
    disabled,
    hasError,
    ariaLabel,
    isRequired,
    dataTestId,
  } = props;

  // Map the flat value list onto the option objects Stencil renders and
  // compares. Unknown values are dropped rather than synthesized: a
  // value with no matching option cannot be rendered or de-selected, so
  // surfacing it as a phantom tag would strand the auditor. Memoised so
  // the selected-options array identity is stable across renders (the
  // reference-compare caveat above).
  const selectedOptions = useMemo(() => {
    const chosen = new Set(values);
    return options.filter((option) => chosen.has(option.value));
  }, [options, values]);

  const renderLabel = useCallback(
    (option: unknown): string => (option as OptionSelectItem).label,
    [],
  );

  // Same `label OR value` search override as OptionSelect: Stencil's
  // default filter matches only the rendered label, silently excluding
  // an id-shaped `value`. Memoised on `options` so the identity is
  // stable — Stencil re-runs it only when the query changes.
  const filterOptions = useMemo(
    () =>
      (query: string): OptionSelectItem[] => {
        const trimmed = query.trim().toLocaleLowerCase();
        if (!trimmed) return options as OptionSelectItem[];
        return (options as OptionSelectItem[]).filter(
          (option) =>
            option.label.toLocaleLowerCase().includes(trimmed) ||
            option.value.toLocaleLowerCase().includes(trimmed),
        );
      },
    [options],
  );

  const handleChange = useCallback(
    (option: unknown): void => {
      // With `isMulti`, Stencil emits the FULL new selected-option
      // array (not the single toggled option). Narrow, then project to
      // the flat value list the wire format expects. The empty-array
      // clear flows through untouched — an empty selection is a real
      // "cleared" state, which the caller serializes to an empty value.
      const picked = (option as readonly OptionSelectItem[] | null) ?? NO_SELECTION;
      onChange(picked.map((item) => item.value));
    },
    [onChange],
  );

  // Conditional spreads throughout: `exactOptionalPropertyTypes`
  // refuses an explicit `undefined` for these optional props, so the
  // keys are omitted entirely when the caller didn't supply them.
  return (
    <Select
      isMulti
      options={options as OptionSelectItem[]}
      value={selectedOptions}
      layout={SelectLayout.Custom}
      onChange={handleChange}
      valueAccessor="value"
      renderOption={renderLabel}
      renderSelectedValue={renderLabel}
      isSearchable={options.length >= SEARCHABLE_SELECT_THRESHOLD}
      filterOptions={filterOptions}
      {...(onBlur !== undefined ? { onBlur } : {})}
      {...(placeholder !== undefined ? { placeholder } : {})}
      {...(disabled !== undefined ? { disabled } : {})}
      {...(hasError !== undefined ? { error: hasError } : {})}
      {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
      {...(isRequired !== undefined ? { 'aria-required': isRequired } : {})}
      {...(hasError !== undefined ? { 'aria-invalid': hasError } : {})}
      {...(dataTestId !== undefined ? { 'data-testid': dataTestId } : {})}
    />
  );
}
